import { Field as ChakraField, Input, VisuallyHidden } from "@chakra-ui/react"
import { FieldConfig } from "formik"
import { Fragment, forwardRef } from "react"
import FormField from "./FormField"

export type FieldChildren =
  | React.FC<{ field: React.ReactNode }>
  | null
  | undefined

const ROUNDED_BORDER_RADIUS = "20px"
type RoundedProp = React.ComponentProps<typeof Input>["rounded"] | boolean

type FieldOwnProps = {
  label?: string
  hideLabel?: boolean
  rounded?: RoundedProp
  children?: FieldChildren
}

export type FieldProps<
  TAs extends React.ElementType = typeof Input,
  TName extends string = string
> = FieldOwnProps &
  Omit<FieldConfig, "as" | "children" | "name" | "component"> &
  Omit<
    React.ComponentPropsWithoutRef<TAs>,
    keyof FieldOwnProps | "children" | "as" | "name"
  > & {
    name: TName
    as?: TAs
  }

const FieldInner = forwardRef(function Field(
  {
    children,
    label,
    hideLabel,
    rounded,
    css,
    ...props
  }: FieldProps,
  ref: React.ForwardedRef<HTMLElement>
): JSX.Element {
  const field = (
    <FormField.Field
      ref={ref}
      as={Input}
      id={props.name}
      variant="subtle"
      css={{
        ...(rounded && { borderRadius: ROUNDED_BORDER_RADIUS }),
        ...css
      }}
      {...props}
    />
  )

  const MaybeHideLabel = hideLabel ? VisuallyHidden : Fragment

  return (
    <FormField.Provider name={props.name}>
      <FormField.Control>
        {!!label && (
          <MaybeHideLabel>
            <ChakraField.Label mb={1} color="blackAlpha.900">
              {label}
            </ChakraField.Label>
          </MaybeHideLabel>
        )}
        {children ? children({ field }) : field}
        <FormField.ErrorMessage />
      </FormField.Control>
    </FormField.Provider>
  )
})

// Cast preserves polymorphic `as` inference that forwardRef erases.
const Field = FieldInner as <
  TAs extends React.ElementType = typeof Input,
  TName extends string = string
>(
  props: FieldProps<TAs, TName> & { ref?: React.Ref<Element> }
) => JSX.Element

export default Field
